import React, { useState } from "react";
import * as finService from "../../../services/finService";
import useFinOptions from "../useFinOptions";
import useGlContext, { fmtMoney } from "../gl/glContext";
import AccountPicker, { accountFromString } from "../gl/AccountPicker";
import { Badge, DataTable, LedgerBar, QUALIFIER_LABELS, act, hint, linkBtn } from "../gl/glUi";
import { AppDropdown, EmptyState, Field, FormDrawer, LoadState, PrimaryButton, SecondaryButton, SlidePanel, Tabs, cardStyle, inputStyle, todayYmd, useLoad } from "../../../components/ui";

// Finance Setup → Ledgers / Accounting rules / Opening balances.
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((m, i) => ({ label: m, value: i + 1 }));
const LEDGER_ACCOUNTS = [
  ["retained_earnings", "Retained earnings", "Last year's profit is carried here (owners' equity)"],
  ["suspense", "Suspense", "Takes the difference when a system journal does not balance"],
  ["rounding", "Rounding", "Currency conversion rounding"],
  ["fx_realised_gain", "Realised exchange gain", ""],
  ["fx_realised_loss", "Realised exchange loss", ""],
  ["fx_unrealised_gain", "Unrealised exchange gain", "Revaluation"],
  ["fx_unrealised_loss", "Unrealised exchange loss", "Revaluation"],
  ["cta", "Translation adjustment", "Consolidation (later)"],
];

/* ------------------------------ ledgers ------------------------------ */

function LedgerAccounts({ gl, ledger, onHide }) {
  const [values, setValues] = useState(() => Object.fromEntries(LEDGER_ACCOUNTS.map(([k]) => [k, ledger.accounts[k] ? accountFromString(gl.structure, ledger.accounts[k]) : null])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const accounts = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v && v.ACCOUNT ? v : null]));
    const res = await act(() => finService.saveLedgerAccounts(ledger.id, { accounts }));
    setBusy(false);
    if (res) {
      gl.reload();
      onHide();
    }
  };
  return (
    <SlidePanel
      show
      onHide={() => !busy && onHide()}
      title={`System accounts – ${ledger.name}`}
      width={640}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={busy}>
            Save
          </PrimaryButton>
        </>
      }
    >
      {LEDGER_ACCOUNTS.map(([k, l, h]) => (
        <Field key={k} label={l} hint={h || undefined} required={k === "retained_earnings"}>
          <AccountPicker id={`la-${k}`} structure={gl.structure} companyValue={ledger.company_value} value={values[k]} onChange={(v) => setValues((s) => ({ ...s, [k]: v }))} />
        </Field>
      ))}
    </SlidePanel>
  );
}

function Calendars() {
  const { data, loading, error, reload } = useLoad(() => finService.listCalendars(), []);
  const [adding, setAdding] = useState(false);
  const addYear = async (c) => {
    const fy = window.prompt("Add periods for which fiscal year?", String(new Date().getFullYear() + 1));
    if (fy && (await act(() => finService.addFiscalYear(c.id, Number(fy))))) reload();
  };
  return (
    <>
      <div className="d-flex align-items-center gap-3 mb-2 mt-4">
        <h6 className="mb-0">Calendars</h6>
        <SecondaryButton onClick={() => setAdding(true)}>Add calendar</SecondaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title="No calendars" />}
          columns={[
            { label: "Code", render: (r) => r.code },
            { label: "Name", render: (r) => r.name },
            { label: "Year starts", render: (r) => (MONTHS[r.fy_start_month - 1] || {}).label },
            { label: "Adjusting period", render: (r) => (r.with_adjusting ? "Yes" : "No") },
            { label: "", render: (r) => <button type="button" style={linkBtn} onClick={() => addYear(r)}>Add fiscal year</button> },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={adding}
        title="Add calendar"
        initial={{ fy_start_month: 1, with_adjusting: true }}
        onHide={() => setAdding(false)}
        onDone={reload}
        onSave={(clean, raw) => finService.createCalendar({ ...clean, with_adjusting: !!raw.with_adjusting })}
        fields={[
          { key: "code", label: "Code", required: true, max: 16 },
          { key: "name", label: "Name", required: true, max: 100 },
          { key: "fy_start_month", label: "Fiscal year starts in", type: "select", required: true, options: MONTHS },
          { key: "with_adjusting", label: "Add a year-end adjusting period", type: "checkbox" },
        ]}
      />
    </>
  );
}

export function Ledgers() {
  const gl = useGlContext();
  const opts = useFinOptions(["legal-entities"]);
  const cals = useLoad(() => finService.listCalendars(), []);
  const [adding, setAdding] = useState(false);
  const [accounts, setAccounts] = useState(null);
  const initialise = async () => (await act(() => finService.initialiseGl())) && gl.reload();
  return (
    <LoadState loading={gl.loading} error={gl.error} onRetry={gl.reload}>
      {gl.structures.length === 0 ? (
        <div style={{ ...cardStyle, padding: 20, maxWidth: 640 }}>
          <div style={{ fontSize: 14, marginBottom: 12 }}>The general ledger is not set up yet. This creates the standard chart of accounts (10 segments), a starter list of accounts, the accounting calendar and the journal sources. Nothing you already set up is changed.</div>
          <PrimaryButton onClick={initialise}>Set up the general ledger</PrimaryButton>
        </div>
      ) : (
        <>
          <div className="d-flex align-items-center gap-3 mb-2">
            <PrimaryButton onClick={() => setAdding(true)}>Add ledger</PrimaryButton>
            <span style={hint}>One ledger per legal entity, in its currency.</span>
          </div>
          <DataTable
            rows={gl.ledgers}
            rowKey={(r) => r.code}
            empty={<EmptyState title="No ledgers yet" text="Add a ledger for each legal entity." />}
            columns={[
              { label: "Code", render: (r) => r.code },
              { label: "Name", render: (r) => r.name },
              { label: "Legal entity", render: (r) => r.legal_entity_name },
              { label: "Currency", render: (r) => r.currency },
              { label: "Chart / calendar", render: (r) => `${r.structure_code} / ${r.calendar_code}` },
              { label: "Retained earnings", render: (r) => (r.accounts.retained_earnings ? <span style={{ fontFamily: "monospace", fontSize: 12 }}>{r.accounts.retained_earnings}</span> : <span style={{ color: "#991B1B" }}>Not set</span>) },
              { label: "Status", render: (r) => <Badge status={r.status} /> },
              { label: "", render: (r) => <button type="button" style={linkBtn} onClick={() => setAccounts(r)}>System accounts</button> },
            ]}
          />
          <Calendars />
          <FormDrawer
            show={adding}
            title="Add ledger"
            initial={{}}
            onHide={() => setAdding(false)}
            onDone={gl.reload}
            onSave={(clean) => finService.createLedger(clean)}
            fields={[
              { key: "legal_entity_id", label: "Legal entity", type: "select", required: true, options: opts["legal-entities"] || [], hint: "The ledger takes the legal entity's currency and company segment value" },
              { key: "code", label: "Code", required: true, max: 16 },
              { key: "name", label: "Name", required: true, max: 100 },
              { key: "structure_id", label: "Chart of accounts", type: "select", required: true, options: gl.structures.map((s) => ({ label: `${s.code} – ${s.name}`, value: s.id })) },
              { key: "calendar_id", label: "Calendar", type: "select", required: true, options: (cals.data || []).map((c) => ({ label: `${c.code} – ${c.name}`, value: c.id })) },
            ]}
          />
          {accounts ? <LedgerAccounts gl={gl} ledger={accounts} onHide={() => setAccounts(null)} /> : null}
        </>
      )}
    </LoadState>
  );
}

/* ------------------------------ accounting rules ------------------------------ */

const OPS = [
  { label: "is", value: "EQ" },
  { label: "is not", value: "NE" },
  { label: "is one of (comma separated)", value: "IN" },
];

function RuleEditor({ ledger, rule, lineTypes, onHide, onDone }) {
  const [form, setForm] = useState(rule ? { ...rule, conditions: rule.conditions || [] } : { line_type: "*", segment_qualifier: "ACCOUNT", priority: 100, output_value: "", conditions: [{ attr: "", op: "EQ", value: "" }], status: "ACTIVE" });
  const [busy, setBusy] = useState(false);
  const setCond = (i, patch) => setForm({ ...form, conditions: form.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const save = async () => {
    setBusy(true);
    const res = await act(() => finService.saveAdrRule(ledger.id, rule ? rule.id : null, { ...form, conditions: form.conditions.filter((c) => c.attr) }));
    setBusy(false);
    if (res) {
      onDone();
      onHide();
    }
  };
  return (
    <SlidePanel
      show
      onHide={() => !busy && onHide()}
      title={rule ? "Edit derivation rule" : "New derivation rule"}
      width={640}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={busy}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <div className="d-flex gap-2">
        <Field label="For line type" htmlFor="adr-lt" style={{ flex: 1 }}>
          <AppDropdown id="adr-lt" value={form.line_type} options={[{ label: "Any line (*)", value: "*" }, ...lineTypes.map((l) => ({ label: l, value: l }))]} onChange={(e) => setForm({ ...form, line_type: e.value })} filter />
        </Field>
        <Field label="Set segment" htmlFor="adr-seg" style={{ flex: 1 }}>
          <AppDropdown id="adr-seg" value={form.segment_qualifier} options={Object.entries(QUALIFIER_LABELS).map(([value, label]) => ({ label, value }))} onChange={(e) => setForm({ ...form, segment_qualifier: e.value })} />
        </Field>
      </div>
      <div className="d-flex gap-2">
        <Field label="To value" required htmlFor="adr-out" style={{ flex: 1 }}>
          <input id="adr-out" className="form-control" style={inputStyle} value={form.output_value} onChange={(e) => setForm({ ...form, output_value: e.target.value.toUpperCase() })} />
        </Field>
        <Field label="Priority (lower wins)" htmlFor="adr-pri" style={{ flex: 1 }}>
          <input id="adr-pri" type="number" min={1} className="form-control" style={inputStyle} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
        </Field>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>When (all must match)</div>
      {form.conditions.map((c, i) => (
        <div key={i} className="d-flex gap-2 mb-2">
          <input className="form-control" style={{ ...inputStyle, width: 170 }} placeholder="attribute, e.g. make" aria-label={`Condition ${i + 1} attribute`} value={c.attr} onChange={(e) => setCond(i, { attr: e.target.value.toLowerCase() })} />
          <div style={{ width: 170 }}>
            <AppDropdown id={`adr-op-${i}`} value={c.op} options={OPS} onChange={(e) => setCond(i, { op: e.value })} />
          </div>
          <input className="form-control" style={inputStyle} placeholder="value" aria-label={`Condition ${i + 1} value`} value={c.value} onChange={(e) => setCond(i, { value: e.target.value })} />
          <button type="button" style={linkBtn} onClick={() => setForm({ ...form, conditions: form.conditions.filter((_, j) => j !== i) })}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setForm({ ...form, conditions: [...form.conditions, { attr: "", op: "EQ", value: "" }] })}>
        Add condition
      </button>
      <div style={{ ...hint, marginTop: 12 }}>
        Attributes come from the document: business_unit, location, make, model, stock_type, order_type, pay_type, customer_class, charge_type, tax_code, product_line. No condition means the rule always applies to its line type.
      </div>
    </SlidePanel>
  );
}

export function AccountingRules() {
  const gl = useGlContext();
  const ledger = gl.ledger;
  const { data, loading, error, reload } = useLoad(() => (ledger ? finService.getAccountingRules(ledger.id) : Promise.resolve({ success: true, data: null })), [ledger && ledger.code]);
  const [section, setSection] = useState("defaults");
  const [setting, setSetting] = useState(null);
  const [rule, setRule] = useState(undefined);
  const [acct, setAcct] = useState(null);
  if (!gl.loading && !ledger) return <EmptyState title="No ledger yet" text="Add a ledger under Ledgers first." />;
  const d = data || { event_types: [], line_types: [], default_accounts: [], derivation_rules: [] };
  const lineTypes = [...new Set(d.line_types.map((l) => l.line_type))].sort();
  const current = (lt) => (d.default_accounts.find((a) => a.line_type === lt) || {}).account || null;
  const saveDefault = async () => {
    if (!(await act(() => finService.saveDefaultAccount(ledger.id, setting, acct)))) return;
    setSetting(null);
    reload();
  };
  return (
    <LoadState loading={gl.loading || loading} error={gl.error || error} onRetry={reload}>
      {ledger ? (
        <>
          <LedgerBar gl={gl} />
          <div style={{ ...hint, marginBottom: 12 }}>
            Documents from receivables, payables, the dealership and other modules are turned into journals here: each line type starts from its default account, then derivation rules override single segments (for example the account by vehicle make, or the cost centre by location).
          </div>
          <Tabs
            active={section}
            onChange={setSection}
            tabs={[
              { key: "defaults", label: "Default accounts" },
              { key: "rules", label: "Derivation rules" },
              { key: "events", label: "Event types" },
            ]}
          />
          {section === "defaults" ? (
            <DataTable
              rows={lineTypes}
              rowKey={(r) => r}
              empty={<EmptyState title="No line types yet" text="Line types appear as modules are switched on." />}
              columns={[
                { label: "Line type", render: (lt) => lt },
                { label: "Used by", render: (lt) => [...new Set(d.line_types.filter((l) => l.line_type === lt).map((l) => l.event_type))].join(", ") },
                { label: "Default account", render: (lt) => (current(lt) ? <span style={{ fontFamily: "monospace", fontSize: 12 }}>{current(lt)}</span> : <span style={{ color: "#991B1B" }}>Not set</span>) },
                {
                  label: "",
                  render: (lt) => (
                    <button
                      type="button"
                      style={linkBtn}
                      onClick={() => {
                        setAcct(current(lt) ? accountFromString(gl.structure, current(lt)) : null);
                        setSetting(lt);
                      }}
                    >
                      Set
                    </button>
                  ),
                },
              ]}
            />
          ) : null}
          {section === "rules" ? (
            <>
              <div className="mb-3">
                <PrimaryButton onClick={() => setRule(null)}>Add rule</PrimaryButton>
              </div>
              <DataTable
                rows={d.derivation_rules}
                empty={<EmptyState title="No derivation rules" text="Every line posts to its default account, with the document's business unit, location and cost centre." />}
                columns={[
                  { label: "Line type", render: (r) => r.line_type },
                  { label: "Segment", render: (r) => QUALIFIER_LABELS[r.segment_qualifier] },
                  { label: "When", render: (r) => (r.conditions.length ? r.conditions.map((c) => `${c.attr} ${c.op === "EQ" ? "=" : c.op === "NE" ? "≠" : "in"} ${c.value}`).join(" and ") : "Always") },
                  { label: "Value", render: (r) => r.output_value },
                  { label: "Priority", num: true, render: (r) => r.priority },
                  { label: "Status", render: (r) => <Badge status={r.status} /> },
                  { label: "", render: (r) => <button type="button" style={linkBtn} onClick={() => setRule(r)}>Edit</button> },
                ]}
              />
            </>
          ) : null}
          {section === "events" ? (
            <DataTable
              rows={d.event_types}
              empty={<EmptyState title="No event types" />}
              columns={[
                { label: "Event", render: (r) => r.code },
                { label: "Name", render: (r) => r.name },
                { label: "Period from", render: (r) => r.application },
                { label: "Journal source / category", render: (r) => `${r.source_code} / ${r.category_code}` },
                { label: "Status", render: (r) => <Badge status={r.status} /> },
              ]}
            />
          ) : null}
          {setting ? (
            <SlidePanel
              show
              onHide={() => setSetting(null)}
              title={`Default account – ${setting}`}
              width={560}
              footer={
                <>
                  <SecondaryButton onClick={() => setSetting(null)}>Cancel</SecondaryButton>
                  <PrimaryButton onClick={saveDefault} disabled={!acct || !acct.ACCOUNT}>
                    Save
                  </PrimaryButton>
                </>
              }
            >
              <AccountPicker id="def-acct" structure={gl.structure} companyValue={ledger.company_value} value={acct} onChange={setAcct} />
            </SlidePanel>
          ) : null}
          {rule !== undefined ? <RuleEditor ledger={ledger} rule={rule} lineTypes={lineTypes} onHide={() => setRule(undefined)} onDone={reload} /> : null}
        </>
      ) : null}
    </LoadState>
  );
}

/* ------------------------------ opening balances ------------------------------ */

export function OpeningBalances() {
  const gl = useGlContext();
  const [form, setForm] = useState({ accounting_date: todayYmd(), csv: "", name: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!gl.loading && !gl.ledger) return <EmptyState title="No ledger yet" text="Add a ledger under Ledgers first." />;
  const body = () => ({ ledger_id: gl.ledger.id, ...form, name: form.name || undefined });
  const check = async () => {
    setBusy(true);
    const res = await finService.previewOpening(body()).catch((e) => ({ success: false, message: e.message }));
    setBusy(false);
    if (res?.success) setPreview(res.data);
    else await act(async () => res);
  };
  const post = async () => {
    if (!window.confirm("Post the opening balances journal?")) return;
    setBusy(true);
    const res = await act(() => finService.importOpening(body()));
    setBusy(false);
    if (res) setPreview(null);
  };
  const readFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, csv: String(reader.result || "") }));
      setPreview(null);
    };
    reader.readAsText(file);
  };
  return (
    <LoadState loading={gl.loading} error={gl.error} onRetry={gl.reload}>
      {gl.ledger ? (
        <div style={{ ...cardStyle, padding: 20, maxWidth: 820 }}>
          <LedgerBar gl={gl} />
          <div style={{ ...hint, marginBottom: 12 }}>
            Load the trial balance you are moving from as one posted journal. CSV columns: <code>account,debit,credit,description</code>; the account is the full combination ({gl.structure ? gl.structure.segments.map((s) => s.name).join(gl.structure.delimiter) : ""}). The accounting date's GL period must be open.
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Field label="Accounting date" required htmlFor="ob-date">
              <input id="ob-date" type="date" className="form-control" style={inputStyle} value={form.accounting_date} onChange={(e) => {
                  setForm({ ...form, accounting_date: e.target.value });
                  setPreview(null);
                }} />
            </Field>
            <Field label="Journal name" htmlFor="ob-name" style={{ flex: 1, minWidth: 240 }}>
              <input id="ob-name" className="form-control" style={inputStyle} placeholder={`Opening balances ${form.accounting_date}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
          </div>
          <Field label="CSV file" htmlFor="ob-file">
            <input id="ob-file" type="file" accept=".csv,text/csv" className="form-control" onChange={(e) => e.target.files[0] && readFile(e.target.files[0])} />
          </Field>
          <Field label="…or paste it" htmlFor="ob-csv">
            <textarea id="ob-csv" className="form-control" rows={8} style={{ fontFamily: "monospace", fontSize: 12 }} value={form.csv} onChange={(e) => {
                setForm({ ...form, csv: e.target.value });
                setPreview(null);
              }} />
          </Field>
          <div className="d-flex gap-2">
            <SecondaryButton onClick={check} disabled={busy || !form.csv}>
              Check
            </SecondaryButton>
            <PrimaryButton onClick={post} disabled={busy || !preview || preview.errors.length > 0}>
              Post opening balances
            </PrimaryButton>
          </div>
          {preview ? (
            <div className="mt-3" style={{ fontSize: 14 }}>
              {preview.errors.length ? (
                <div style={{ color: "#991B1B" }}>
                  {preview.errors.map((e, i) => (
                    <div key={i}>
                      {e.row ? `Row ${e.row}: ` : ""}
                      {e.message}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#065F46" }}>
                  {preview.lines} lines, balanced: debits {fmtMoney(preview.totals.entered_dr)} = credits {fmtMoney(preview.totals.entered_cr)} {preview.currency}.
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </LoadState>
  );
}
